import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import { addLeadingZeros } from '@elliemae/ds-utilities/timeUtils';
import { parseInt, isNaN } from '@elliemae/ds-utilities/utils';
import moment from 'moment';

var currentYear = new Date().getFullYear();

var getNextSibling = function getNextSibling(elem, selector) {
  // Get the next sibling element
  var sibling = elem.nextElementSibling; // If there's no selector, return the first sibling

  if (!selector) return sibling; // If the sibling matches our selector, use it
  // If not, jump to the next sibling and continue the loop

  while (sibling) {
    if (sibling.matches(selector)) return sibling;
    sibling = sibling.nextElementSibling;
  }

  return sibling;
};

var getPreviousSibling = function getPreviousSibling(elem, selector) {
  var sibling = elem.previousElementSibling;
  if (!selector) return sibling;

  while (sibling) {
    if (sibling.matches(selector)) return sibling;
    sibling = sibling.previousElementSibling;
  }

  return sibling;
};

var isArrowIncrementDecrement = function isArrowIncrementDecrement(key) {
  return key === 'ArrowDown' || key === 'ArrowUp';
}; // TODO remove and keep `true` behavior ( PUI-4141 )

var setNativeValue = function setNativeValue(element, value) {
  if (!Object.getOwnPropertyDescriptor(element, 'value')) return;
  var valueSetter = Object.getOwnPropertyDescriptor(element, 'value').set;
  var prototype = Object.getPrototypeOf(element);
  var prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value').set;

  if (valueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter.call(element, value);
  } else {
    valueSetter.call(element, value);
  }

  element.dispatchEvent(new Event('input', {
    bubbles: true
  }));
};

var formatTimeNumber = function formatTimeNumber(value, shouldAddLeadingZeros, leadingZeros) {
  if (value === null || value === undefined) return '';
  return shouldAddLeadingZeros ? addLeadingZeros(leadingZeros)(value) : String(value);
};

var getFormattedDay = function getFormattedDay(value) {
  var v = value.length > 2 ? value.slice(-2) : value;
  var parsedValue = parseInt(v, 10);
  if (parsedValue > 31) return '31';
  return formatTimeNumber(parseTimeNumberFromText(v), true, 2);
};
var getShouldShortcircuitDay = function getShouldShortcircuitDay(value) {
  var v = value.length > 2 ? value.slice(-2) : value;
  var parsedValue = parseInt(v, 10);
  if (parsedValue > 31) return true; // if the first input is in range 4-9 then there is no other option
  // so we auto advance

  if (parsedValue > 3 && parsedValue < 10) return true;
  return false;
};
var getFormattedYear = function getFormattedYear(value) {
  var v = value.length > 4 ? value.slice(-4) : value;
  if (parseInt(v) > 9999) return '9999';
  return formatTimeNumber(parseTimeNumberFromText(v), true, 4);
};
var getFormattedMonth = function getFormattedMonth(value) {
  var v = value.length > 2 ? value.slice(-2) : value;
  var parsedValue = parseInt(v, 10);
  if (parsedValue > 12) return '12';
  return formatTimeNumber(parseTimeNumberFromText(v), true, 2);
};
var getShouldShortcircuitMonth = function getShouldShortcircuitMonth(value) {
  var v = value.length > 2 ? value.slice(-2) : value;
  var parsedValue = parseInt(v, 10);
  if (parsedValue > 12) return true; // if the first input is in range 2-9 then there is no other option
  // so we auto advance

  if (parsedValue > 1 && parsedValue < 10) return true;
  return false;
};
var parseTimeNumberFromText = function parseTimeNumberFromText(stringValue) {
  var parsedInt = parseInt(stringValue);
  if (isNaN(parsedInt)) return null;
  return parsedInt;
}; // trigger onBlur for entire DateInput control if focus is not in day, month, year

var triggerOnBlur = function triggerOnBlur(blurEvent) {
  var onBlur = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function () {};

  var _ref = blurEvent || {},
      _ref$target = _ref.target,
      currentEl = _ref$target === void 0 ? {} : _ref$target,
      relatedTarget = _ref.relatedTarget;

  var nextElement = currentEl.nextElementSibling;

  var _ref2 = nextElement || {},
      nextElementSibling = _ref2.nextElementSibling; // relatedTarget points to the element that is currently having focus


  if (!nextElementSibling || nextElementSibling !== relatedTarget) {
    onBlur(blurEvent);
  }
};
var focusNextInput = function focusNextInput(currentEl) {
  getNextSibling(currentEl, 'input').focus();
};
var focusPreviousInput = function focusPreviousInput(currentEl) {
  getPreviousSibling(currentEl, 'input').focus();
};
var getValidTimeNumber = function getValidTimeNumber(_ref3, number) {
  var min = _ref3.min,
      max = _ref3.max;
  var valueString = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
  var onError = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : function () {
    return null;
  };
  if (valueString === '0' || number === 0) return null;
  if (number === undefined || number === null) return null;

  if (number > max) {
    onError();
    return max;
  }

  if (number < min) {
    onError();
    return min;
  }

  return number;
};
var shouldFocusNextInput = function shouldFocusNextInput(max) {
  var number = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var stringValue = arguments.length > 2 ? arguments[2] : undefined;
  if (stringValue.length > 1) return true;
  return number * 10 > max;
};
var getDateValuesFromTime = function getDateValuesFromTime(time, format) {
  if (!time || !time.format) return {};
  time.format('MM');
  time.format('DD');
  time.format('YYYY');
  return {
    month: getFormattedMonth(format),
    day: getFormattedDay(format),
    year: getFormattedYear(format)
  };
};
var resetTimeValues = function resetTimeValues() {
  return {
    month: '',
    day: '',
    year: ''
  };
};
var onInputFocus = function onInputFocus(e) {
  e.preventDefault();
  e.stopPropagation(); // we select the whole value to be able to override the value on first key
  // we don't use selection color to be consistent with the highlight of the input

  e.target.select();
};
var getNextTimeValue = function getNextTimeValue(value, target, yearMinRange, yearMaxRange) {
  var step = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 1;
  var incrementing = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : true;
  var parsedValue = parseTimeNumberFromText(value);
  var result = incrementing ? parsedValue + step : parsedValue - step;

  if (target === 'year') {
    if (result < yearMinRange) result = currentYear;
    if (result > yearMaxRange) result = currentYear;
  }

  return result;
};
var focusNextInputIfNeeded = function focusNextInputIfNeeded(currentEl, max, number, stringValue, currentKey) {
  if (shouldFocusNextInput(max, number, stringValue) && !isArrowIncrementDecrement(currentKey)) {
    focusNextInput(currentEl);
  }
};
var commonInputProps = function commonInputProps() {
  var digits = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 2;
  var onKeyDown = arguments.length > 1 ? arguments[1] : undefined;
  var placeholder = arguments.length > 2 ? arguments[2] : undefined;
  var props = {
    pattern: '[0-9]*',
    placeholder: placeholder,
    type: 'text',
    onKeyDown: onKeyDown,
    onFocus: onInputFocus
  };
  if (digits === 4) props.placeholder = 'YYYY';
  return props;
};
var handleCompletedDate = function handleCompletedDate(time, date, show, INTERNAL_V2_NO_MUTATION) {
  // clone time to avoid mutation when INTERNAL_V2_NO_MUTATION is true
  var newDate = INTERNAL_V2_NO_MUTATION ? time && moment(time) || moment() : time || moment();
  var year = date.year,
      month = date.month,
      day = date.day;
  var showYear = show.showYear,
      showMonth = show.showMonth,
      showDay = show.showDay;
  if (time && !year && !month && !day) return '';
  newDate.value = "".concat(year, "-").concat(month, "-").concat(day);
  if (showMonth && month) newDate.month(parseInt(month - 1, 10));
  if (showDay && day) newDate.date(parseInt(day, 10));
  if (showYear && year) newDate.year(parseInt(year, 10));
  return newDate;
};
var isTimeCompletelySet = function isTimeCompletelySet(date, show, range) {
  var neededValues = [];
  var year = date.year,
      month = date.month,
      day = date.day;
  var showYear = show.showYear,
      showMonth = show.showMonth,
      showDay = show.showDay;
  var yearMinRange = range.yearMinRange,
      yearMaxRange = range.yearMaxRange;
  if (showMonth) neededValues = [].concat(_toConsumableArray(neededValues), [month || null]);
  if (showDay) neededValues = [].concat(_toConsumableArray(neededValues), [day || null]);
  if (showYear) neededValues = [].concat(_toConsumableArray(neededValues), [year || null]);
  var isValidYear = year < yearMaxRange && year > yearMinRange;
  return !neededValues.some(function (value) {
    return !value;
  }) && isValidYear;
};

export { commonInputProps, focusNextInput, focusNextInputIfNeeded, focusPreviousInput, getDateValuesFromTime, getFormattedDay, getFormattedMonth, getFormattedYear, getNextTimeValue, getShouldShortcircuitDay, getShouldShortcircuitMonth, getValidTimeNumber, handleCompletedDate, isArrowIncrementDecrement, isTimeCompletelySet, onInputFocus, parseTimeNumberFromText, resetTimeValues, setNativeValue, shouldFocusNextInput, triggerOnBlur };
//# sourceMappingURL=utils.js.map
